/**
 * Pequeñas Puntadas — customer-facing content and image mapping.
 *
 * Every image has a descriptive `file` name. While `src` is empty, the page
 * shows a prepared frame with that file name; paste the uploaded image URL
 * into `src` to replace it.
 *
 * Images are light WebP files hosted in the store's Impultienda resources.
 */

export interface SiteImage {
  src: string
  file: string
  alt: string
  width: number
  height: number
  /** Optional lighter file for thumbnails; the full-resolution `src` is used to enlarge. */
  thumbSrc?: string
}

export interface ManualPage extends SiteImage {
  caption: string
}

export interface InteriorSample extends SiteImage {
  title: string
  volume: string
  model: string
  description: string
}

export interface Volume {
  number: number
  summary: string
  models: string[]
  cover: SiteImage
}

export interface Bonus {
  number: number
  title: string
  benefit: string
  /** Reference value shown struck through; bonuses are included at no extra cost. */
  compareAt: string
  cover: SiteImage
}

export interface Faq {
  question: string
  answer: string
  /** Shows the size/age table under the answer. */
  sizeTable?: boolean
}

/** Image host (Impultienda resources, WebP). */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/41acc9e0-0163-4a83-8a16-33e698185c3e/`

export const BRAND = {
  name: 'Pequeñas Puntadas™',
  descriptor: 'Moldes Multi-talle de ropa infantil para coser, regalar o vender',
} as const

export const OFFER_ANCHOR = '#oferta'
export const PREVIEW_ANCHOR = '#modelo'

export const GALLERY_ANCHOR = '#prendas'

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Talles', href: '#talles' },
  { label: 'Prendas', href: '#prendas' },
  { label: 'Por dentro', href: '#modelo' },
  { label: 'Bonos', href: '#bonos' },
  { label: 'Preguntas', href: '#preguntas' },
]

/* ——— Sizes by height, with approximate age ——— */

export const SIZE_AGES: Array<{ height: string; age: string }> = [
  { height: '50 cm', age: 'Recién nacido' },
  { height: '56 cm', age: '1 a 2 meses' },
  { height: '62 cm', age: '2 a 4 meses' },
  { height: '68 cm', age: '4 a 6 meses' },
  { height: '74 cm', age: '6 a 9 meses' },
  { height: '80 cm', age: '9 a 12 meses' },
  { height: '86 cm', age: '12 a 18 meses' },
  { height: '92 cm', age: '18 a 24 meses' },
  { height: '98 cm', age: '2 a 3 años' },
  { height: '104 cm', age: '3 a 4 años' },
]

export const SIZE_NOTE =
  'La edad es orientativa: cada chico crece a su ritmo. Elegí el talle por estatura; el manual te explica cómo medir.'


/* ——— Finished-garment gallery (digital visualizations, not customer results) ——— */

export interface Garment {
  title: string
  caption: string
  image: SiteImage
}

const GARMENT_SIZE = { width: 1254, height: 1254 }

const garment = (title: string, caption: string, src: string, alt: string): Garment => ({
  title,
  caption,
  image: { src, file: src.split('/').pop() ?? '', alt: `Visualización ilustrativa: ${alt}`, ...GARMENT_SIZE },
})

/** 10 of the 31 models, one distinct image each. */
export const GARMENTS: Garment[] = [
  garment(
    'Vestido con cuello y lazo',
    'V2-02 · Volumen 2',
    `${IMG}1792693072348_landing-modelo-3-8f32d0c2.webp`,
    'vestido rosa viejo con cuello, mangas cortas, falda fruncida y lazos a los costados',
  ),
  garment(
    'Jardinero corto',
    'Volumen 1',
    `${IMG}1792692921304_landing-modelo-1-fe889219.webp`,
    'jardinero corto verde salvia con tiradores y cintura elastizada',
  ),
  garment(
    'Ranita floral',
    'V5-02 · Volumen 5',
    `${IMG}1792693078500_landing-modelo-2-fc30f40c.webp`,
    'ranita floral color crema con tiradores cruzados',
  ),
  garment(
    'Vestido de falda doble',
    'V2-01 · Volumen 2',
    `${IMG}1792693085406_landing-modelo-4-8f9ce310.webp`,
    'vestido amarillo suave con mangas cortas y dos capas de falda superpuestas',
  ),
  garment(
    'Vestido de breteles escalonado',
    'V2-03 · Volumen 2',
    `${IMG}1792693091101_landing-modelo-6-9b36d14d.webp`,
    'vestido celeste a rayas blancas con breteles y tres niveles de falda',
  ),
  garment(
    'Vestido de mangas abullonadas',
    'V2-04 · Volumen 2',
    `${IMG}1792693097385_landing-modelo-5-d46e5543.webp`,
    'vestido lila con mangas abullonadas y puños estrechos',
  ),
  garment(
    'Vestido de encaje con forro',
    'V2-05 · Volumen 2',
    `${IMG}1792693103171_landing-modelo-11-289b7e90.webp`,
    'vestido marfil con cuerpo liso, mangas cortas y falda de encaje',
  ),
  garment(
    'Vestido con cuello adornado y botones',
    'V3-01 · Volumen 3',
    `${IMG}1792693109171_landing-modelo-8-f04d3c01.webp`,
    'vestido durazno con pequeñas flores y cuello con puntilla',
  ),
  garment(
    'Vestidito con bombachudo y cofia',
    'V4-01 · Volumen 4',
    `${IMG}1792693115024_landing-modelo-10-2d7ea250.webp`,
    'conjunto crema con flores celestes: vestido de breteles con falda escalonada, bombachudo y cofia',
  ),
  garment(
    'Blusa abullonada y bombachudo',
    'V3-02 · Volumen 3',
    `${IMG}1792693120883_landing-modelo-9-65274844.webp`,
    'conjunto de dos piezas a cuadritos verde salvia y crema: blusa de mangas abullonadas y bombachudo',
  ),
]

export const GALLERY_NOTE =
  'Visualizaciones ilustrativas de los diseños. Las telas, colores y terminaciones pueden variar según tu confección.'

/* ——— Sample model: Volumen 2 · Vestido con cuello y lazo ——— */

export const SAMPLE_MODEL = {
  name: 'Vestido con cuello y lazo',
  volume: 'Volumen 2',
} as const

/** Real manual pages (letter-like ratio) and pattern pages (A4 ratio). */
const MANUAL_SIZE = { width: 1224, height: 1584 }
const PATTERN_SIZE = { width: 1191, height: 1684 }

const MANUAL_ASSETS: Array<{ src: string; caption: string; page: number }> = [
  {
    src: `${IMG}1792693151226_landing-manual-pag-01-c125662c.webp`,
    caption: 'Presentación del modelo',
    page: 9,
  },
  {
    src: `${IMG}1792693156192_landing-manual-pag-02-4425b160.webp`,
    caption: 'Inventario de piezas',
    page: 10,
  },
  {
    src: `${IMG}1792693161210_landing-manual-pag-03-c57538c6.webp`,
    caption: 'Preparar y cortar',
    page: 11,
  },
  {
    src: `${IMG}1792693166273_landing-manual-pag-04-1a6e3bc0.webp`,
    caption: 'Armado: primera parte',
    page: 12,
  },
  {
    src: `${IMG}1792693171360_landing-manual-pag-05-7e64fae8.webp`,
    caption: 'Armado: segunda parte',
    page: 13,
  },
  {
    src: `${IMG}1792693176357_landing-manual-pag-06-334cc10d.webp`,
    caption: 'Terminaciones y control',
    page: 14,
  },
]

export const MANUAL_PAGES: ManualPage[] = MANUAL_ASSETS.map(({ src, caption, page }, index) => ({
  src,
  file: `v2-vestido-con-cuello-y-lazo-manual-pag-${String(index + 1).padStart(2, '0')}.webp`,
  alt: `Manual del Volumen 2, página ${page}: V2-02 Vestido con cuello y lazo — ${caption}`,
  caption,
  ...MANUAL_SIZE,
}))

export const PATTERN_PAGES: ManualPage[] = [
  {
    src: `${IMG}1792693181334_landing-molde-mapa-503e3fb7.webp`,
    file: 'v2-vestido-con-cuello-y-lazo-molde-mapa.webp',
    alt: 'PDF de moldes del Volumen 2, página 37: mapa de hojas del V2-02 Vestido con cuello y lazo',
    caption: 'Esquema de armado · pág. 37',
    ...PATTERN_SIZE,
  },
  {
    src: `${IMG}1792693185973_landing-molde-a1-6a177818.webp`,
    file: 'v2-vestido-con-cuello-y-lazo-molde-a1.webp',
    alt: 'PDF de moldes del Volumen 2, página 38: hoja A1 del V2-02 Vestido con cuello y lazo, con las líneas de los diez talles',
    caption: 'Hoja A1 · pág. 38',
    ...PATTERN_SIZE,
  },
  {
    src: `${IMG}1792693190543_landing-molde-a2-6bfdd68b.webp`,
    file: 'v2-vestido-con-cuello-y-lazo-molde-a2.webp',
    alt: 'PDF de moldes del Volumen 2, página 39: hoja A2 del V2-02 Vestido con cuello y lazo, con las líneas de los diez talles',
    caption: 'Hoja A2 · pág. 39',
    ...PATTERN_SIZE,
  },
]

/* ——— Interior pages from different models ——— */

export const INTERIOR_SAMPLES: InteriorSample[] = [
  {
    src: `${IMG}1792693216664_landing-interior-v5-ranita-1ae9366e.webp`,
    file: 'interior-v5-ranita-floral-presentacion.webp',
    alt: 'Manual del Volumen 5, página 9: presentación del modelo V5-02 Ranita floral con gorrito',
    title: 'Presentación del modelo',
    volume: 'Volumen 5',
    model: 'V5-02 · Ranita floral con gorrito',
    description: 'Manual de confección, página 9.',
    ...MANUAL_SIZE,
  },
  {
    src: `${IMG}1792693221494_landing-interior-v4-buzo-da5f3376.webp`,
    file: 'interior-v4-buzo-piezas.webp',
    alt: 'Manual del Volumen 4, página 16: inventario de piezas del modelo V4-03 Buzo con capucha y pantalón',
    title: 'Inventario de piezas',
    volume: 'Volumen 4',
    model: 'V4-03 · Buzo con capucha y pantalón',
    description: 'Manual de confección, página 16.',
    ...MANUAL_SIZE,
  },
  {
    src: `${IMG}1792693225692_landing-interior-v3-blusa-c4659af2.webp`,
    file: 'interior-v3-blusa-confeccion.webp',
    alt: 'Manual del Volumen 3, página 12: armado, primera parte, del modelo V3-02 Blusa abullonada y bombachudo',
    title: 'Armado: primera parte',
    volume: 'Volumen 3',
    model: 'V3-02 · Blusa abullonada y bombachudo',
    description: 'Manual de confección, página 12.',
    ...MANUAL_SIZE,
  },
  {
    src: `${IMG}1792693230469_landing-interior-v1-enterito-e67a6f5d.webp`,
    file: 'interior-v1-enterito-cruzado-molde.webp',
    alt: 'PDF de moldes del Volumen 1, página 107: hoja A1 del molde V1-07 Enterito cruzado',
    title: 'Hoja real del molde',
    volume: 'Volumen 1',
    model: 'V1-07 · Enterito cruzado',
    description: 'PDF de moldes, página 107.',
    ...PATTERN_SIZE,
  },
]

/* ——— The six volumes ——— */

/** Real cover artwork, shown whole at its natural 4:5 proportion. */
const COVER_SIZE = { width: 1224, height: 1528 }

const COVER_ASSETS: Record<number, { src: string; title: string }> = {
  1: {
    src: `${IMG}1792693236426_landing-portada-volumen-01-b33ebd62.webp`,
    title: 'Básicos, ranitas y enteritos',
  },
  2: {
    src: `${IMG}1792693241458_landing-portada-volumen-02-674e70d8.webp`,
    title: 'Vestiditos para distintas ocasiones',
  },
  3: {
    src: `${IMG}1792693247068_landing-portada-volumen-03-8eea176b.webp`,
    title: 'Blusas y conjuntos livianos',
  },
  4: {
    src: `${IMG}1792693252940_landing-portada-volumen-04-d433e5bf.webp`,
    title: 'Conjuntos para salir y jugar',
  },
  5: {
    src: `${IMG}1792693258458_landing-portada-volumen-05-7c03ddd1.webp`,
    title: 'Ranitas con detalles especiales',
  },
  6: {
    src: `${IMG}1792693263699_landing-portada-volumen-06-9d821d97.webp`,
    title: 'Enterito y complementos infantiles',
  },
}

const cover = (n: number): SiteImage => ({
  src: COVER_ASSETS[n].src,
  file: `portada-volumen-${String(n).padStart(2, '0')}.webp`,
  alt: `Portada del Volumen ${n} de Pequeñas Puntadas™: ${COVER_ASSETS[n].title}`,
  ...COVER_SIZE,
})

/** Complete product mockup (hero + offer). Shown whole, never re-framed. */
export const COLLECTION_MOCKUP: SiteImage = {
  src: `${IMG}1792693126626_landing-mockup-coleccion-dc9f7a06.webp`,
  file: 'mockup-coleccion.webp',
  alt: 'Colección digital Pequeñas Puntadas™: las seis portadas de los volúmenes, una tablet con la página del Vestido con cuello y lazo y una hoja de molde A4 impresa',
  width: 1254,
  height: 1254,
}

export const VOLUMES: Volume[] = [
  {
    number: 1,
    summary: 'Jardinero, ranitas, enteritos y body.',
    cover: cover(1),
    models: [
      'Jardinero corto de pechera sencilla',
      'Ranita de tiradores cruzados',
      'Ranita con volados y puntilla',
      'Enterito con botones delanteros',
      'Enterito corto con cuello',
      'Body sin mangas con gorrito',
      'Enterito cruzado',
      'Enterito largo con pies',
    ],
  },
  {
    number: 2,
    summary: 'Vestidos con distintos cuellos, mangas y faldas.',
    cover: cover(2),
    models: [
      'Vestido de falda doble',
      'Vestido con cuello y lazo',
      'Vestido de breteles escalonado',
      'Vestido con mangas abullonadas',
      'Vestido de encaje con forro',
    ],
  },
  {
    number: 3,
    summary: 'Vestido, blusas y conjuntos de dos piezas.',
    cover: cover(3),
    models: [
      'Vestido con cuello adornado y botones',
      'Blusa abullonada y bombachudo',
      'Blusa de breteles y bombachudo',
      'Blusa con volado y short con tiradores',
      'Conjunto playero de blusa y short',
    ],
  },
  {
    number: 4,
    summary: 'Conjuntos, buzo con capucha y jardineros.',
    cover: cover(4),
    models: [
      'Vestidito con bombachudo y cofia',
      'Camisa y short con tiradores',
      'Buzo con capucha y pantalón',
      'Body camisero y jardinero largo',
      'Body polo y jardinero corto',
    ],
  },
  {
    number: 5,
    summary: 'Ranitas con faldita, volados y gorrito.',
    cover: cover(5),
    models: [
      'Ranita con faldita y moño',
      'Ranita floral con gorrito',
      'Ranita con mangas caídas',
      'Ranita con faldita de volados',
    ],
  },
  {
    number: 6,
    summary: 'Enterito, bombachudo y accesorios.',
    cover: cover(6),
    models: [
      'Enterito camisero con cierre frontal',
      'Bombachudo con volados escalonados',
      'Gorrito y babero bandana',
      'Pañal de tela con broches',
    ],
  },
]

/* ——— Bonuses ——— */

/** Square mockup images that already contain the complete cover. */
const BONUS_SIZE = { width: 1024, height: 1024 }

const bonus = (number: number, title: string, benefit: string, compareAt: string, src: string): Bonus => ({
  number,
  title,
  benefit,
  compareAt,
  cover: {
    src,
    file: `portada-bono-${String(number).padStart(2, '0')}.webp`,
    alt: `Portada del bono ${number}: ${title}`,
    ...BONUS_SIZE,
  },
})

export const BONUSES: Bonus[] = [
  bonus(
    1,
    'Guía de impresión y armado de moldes A4™',
    'Para imprimir a escala real y unir las hojas sin perderte.',
    '$14.990',
    `${IMG}1792693279986_landing-portada-bono-01-2ba0ac46.webp`,
  ),
  bonus(
    2,
    'Cuaderno de medidas, talles y ajustes™',
    'Para anotar las medidas de cada chico y elegir el talle con más seguridad.',
    '$9.990',
    `${IMG}1792693285068_landing-portada-bono-02-1d334555.webp`,
  ),
  bonus(
    3,
    'Checklist de preparación y corte™',
    'Para tener tela, piezas y materiales listos antes de cortar.',
    '$7.990',
    `${IMG}1792693292682_landing-portada-bono-03-17873dfe.webp`,
  ),
  bonus(
    4,
    'Guía práctica de telas y terminaciones™',
    'Para elegir telas y terminaciones según la prenda que vas a coser.',
    '$7.990',
    `${IMG}1792693297663_landing-portada-bono-04-f66fca90.webp`,
  ),
  bonus(
    5,
    'Cuaderno de costos y precios de tus prendas™',
    'Para registrar tus costos y definir el precio de lo que vendés.',
    '$9.990',
    `${IMG}1792693302677_landing-portada-bono-05-4b372498.webp`,
  ),
]

/* ——— Offer ——— */

export const OFFER_FEATURES: string[] = [
  'Ideal para principiantes',
  '310 moldes (31 modelos × 10 talles)',
  'Moldes A4 listos para imprimir',
  '10 talles por estatura',
  'Manuales de confección paso a paso',
  '5 bonos de regalo',
]

/* ——— FAQ ——— */

export const FAQS: Faq[] = [
  {
    question: '¿Qué incluye la colección?',
    answer:
      '310 moldes (31 modelos × 10 talles) distribuidos en seis volúmenes. Cada volumen tiene su manual de confección y su PDF de moldes A4, por separado: en total, 12 PDF principales. Además, sumás cinco bonos complementarios sin cargo adicional.',
  },
  {
    question: '¿Qué talles trae?',
    answer:
      'Diez talles por estatura, de recién nacido a 4 años. Los accesorios de cabeza, como gorritos y cofias, se eligen por contorno de cabeza.',
    sizeTable: true,
  },
  {
    question: '¿Cómo elijo el talle?',
    answer:
      'Medí la estatura de la nena o el nene y elegí el talle más cercano. Si queda entre dos talles, compará también otras medidas del cuerpo antes de cortar. La edad sirve solo como referencia general: cada chico crece a su ritmo, por eso conviene guiarse por la estatura y las medidas.',
  },
  {
    question: '¿Puedo imprimir los moldes en casa?',
    answer:
      'Sí. Los moldes se imprimen en hojas A4, a escala real, con cualquier impresora hogareña. Imprimí al 100 % —sin ajustar a la página—, uní las hojas y recortá el talle que necesitás.',
  },
  {
    question: '¿Los moldes incluyen margen de costura?',
    answer: 'Sí. Los moldes ya incluyen un margen de costura de 7 mm.',
  },
  {
    question: '¿Recibo un producto físico?',
    answer:
      'No. Pequeñas Puntadas es una colección digital en PDF. No se envían libros, hojas impresas ni prendas: imprimís los moldes vos y consultás los manuales en tu dispositivo.',
  },
  {
    question: '¿Cómo accedo a los archivos?',
    answer:
      'Después del pago, accedés al enlace de descarga de todos los archivos. Te recomendamos guardarlos en tu dispositivo para tenerlos siempre a mano.',
  },
  {
    question: '¿Puedo consultar los manuales desde el celular?',
    answer:
      'Sí. Los manuales se consultan desde el celular, la tablet o la computadora. Los moldes, en cambio, están pensados para imprimir en A4.',
  },
  {
    question: '¿Los moldes sirven para principiantes?',
    answer:
      '¡Sí! Pequeñas Puntadas está pensado también para quienes están dando sus primeros pasos en la confección de ropa infantil. Los moldes ya vienen preparados para imprimir y cada modelo incluye un manual con instrucciones paso a paso para ayudarte durante la confección.',
  },
  {
    question: '¿Puedo vender las prendas que confeccione?',
    answer:
      'Sí, podés vender las prendas que cosas con estos moldes. Lo que no está permitido es revender, compartir ni redistribuir los archivos PDF.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'

/** WhatsApp contact link (e.g. https://wa.me/549XXXXXXXXXX). While empty, the FAQ shows only the email. */
export const WHATSAPP_URL = ''
